import type { JiraEpicSyncStatus, JiraItemSyncStatus, JiraStoriesSyncStatus } from '@/domain/models/development.js';
import type { TranslateFn } from '@/shared/utils/format-date.js';

export const JIRA_SYNC_POLL_MS = 10_000;
const RETRY_POLL_WINDOW_MS = 2 * 60_000;

export type JiraSyncClock = {
    epic: { status: JiraEpicSyncStatus; nextAttemptAt: string | null };
    stories: { status: JiraStoriesSyncStatus; nextAttemptAt: string | null };
    solicitations: Array<{ status: JiraItemSyncStatus; nextAttemptAt: string | null }>;
};

export const jiraRetryLabel = (translate: TranslateFn, nextAttemptAt: string | null): string => {
    if (nextAttemptAt === null) {
        return '';
    }
    return translate('devDetail.jiraRetryAt', { time: formatJiraRetryTime(new Date(nextAttemptAt)) });
};

export const formatJiraRetryTime = (value: Date): string => {
    const hours = String(value.getHours()).padStart(2, '0');
    const minutes = String(value.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const retryIsSoon = (nextAttemptAt: string | null, now: Date): boolean => {
    if (nextAttemptAt === null) {
        return false;
    }
    const at = new Date(nextAttemptAt).getTime();
    if (Number.isNaN(at)) {
        return false;
    }
    return at - now.getTime() <= RETRY_POLL_WINDOW_MS;
};

export const isJiraSyncPending = (sync: JiraSyncClock, now: Date): boolean => {
    if (sync.epic.status === 'PENDING' || sync.stories.status === 'GENERATING' || sync.stories.status === 'CREATING') {
        return true;
    }
    if (sync.solicitations.some((item) => item.status === 'PENDING')) {
        return true;
    }
    if (sync.epic.status === 'RETRYING' && retryIsSoon(sync.epic.nextAttemptAt, now)) {
        return true;
    }
    if (sync.stories.status === 'RETRYING' && retryIsSoon(sync.stories.nextAttemptAt, now)) {
        return true;
    }
    return sync.solicitations.some((item) => item.status === 'RETRYING' && retryIsSoon(item.nextAttemptAt, now));
};
